import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { User, ShieldCheck, ArrowLeft } from "lucide-react";

const STAFF_ROLES = ["admin", "owner", "manager", "employee"];

export default function Login() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState(null); // null | "customer" | "employee"
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u = await auth.login(form.email, form.password);
      const isStaff = STAFF_ROLES.includes(u.role);
      if (mode === "employee") {
        if (!isStaff) {
          setError("This is not a staff account. Please use the Customer login.");
          await auth.logout();
          return;
        }
        navigate("/admin");
      } else {
        if (isStaff) navigate("/admin");
        else navigate("/account");
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  if (!mode) {
    return (
      <div data-testid="login-chooser" className="max-w-3xl mx-auto px-6 lg:px-10 py-20">
        <div className="text-center">
          <span className="eyebrow text-copper">High Achievers Texas</span>
          <h1 className="font-serif text-4xl sm:text-5xl text-navy mt-3">How are you signing in?</h1>
          <p className="text-charcoal/70 mt-3">Choose the login that fits you.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-12">
          <button
            data-testid="choose-customer-login"
            onClick={() => { setMode("customer"); setError(""); }}
            className="group bg-sand/90 border border-tan/50 p-10 text-left hover:border-copper transition-colors float-card"
          >
            <User className="text-copper" size={30} />
            <h2 className="font-serif text-2xl text-navy mt-5">Customer</h2>
            <p className="text-sm text-charcoal/70 mt-2">Shop products, track orders, and manage your account.</p>
            <span className="inline-block mt-6 text-xs uppercase tracking-[0.2em] text-navy group-hover:text-copper">Continue →</span>
          </button>
          <button
            data-testid="choose-employee-login"
            onClick={() => { setMode("employee"); setError(""); }}
            className="group bg-navy text-white p-10 text-left hover:bg-charcoal transition-colors float-card"
          >
            <ShieldCheck className="text-copper" size={30} />
            <h2 className="font-serif text-2xl text-tan mt-5">Employee / Staff</h2>
            <p className="text-sm text-white/70 mt-2">Manage products, orders, videos, and site content.</p>
            <span className="inline-block mt-6 text-xs uppercase tracking-[0.2em] text-tan group-hover:text-copper">Continue →</span>
          </button>
        </div>
      </div>
    );
  }

  const isEmployee = mode === "employee";

  return (
    <div data-testid="login-page" className="max-w-md mx-auto px-6 lg:px-10 py-20">
      <button
        data-testid="login-back"
        onClick={() => { setMode(null); setError(""); setForm({ email: "", password: "" }); }}
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-charcoal/70 hover:text-copper mb-5"
      >
        <ArrowLeft size={14} /> Back
      </button>
      <div className="bg-sand/90 backdrop-blur-sm border border-tan/50 p-10 float-card">
        <span className="eyebrow text-copper">{isEmployee ? "Staff Access" : "Welcome Back"}</span>
        <h1 className="font-serif text-3xl text-navy mt-3">{isEmployee ? "Employee Sign In" : "Customer Sign In"}</h1>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wide text-charcoal/80">Email</label>
            <input
              data-testid="login-email"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-2 w-full border border-tan/50 px-4 py-3 bg-white focus:border-navy focus:outline-none"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-charcoal/80">Password</label>
            <input
              data-testid="login-password"
              type="password"
              required
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="mt-2 w-full border border-tan/50 px-4 py-3 bg-white focus:border-navy focus:outline-none"
            />
          </div>
          {error && <div data-testid="login-error" className="text-sm text-texasred">{error}</div>}
          <button
            data-testid="login-submit"
            disabled={busy}
            className="w-full bg-navy text-white py-3.5 text-xs tracking-[0.22em] uppercase hover:bg-charcoal disabled:opacity-50"
          >
            {busy ? "Signing in…" : "Sign In"}
          </button>
        </form>
        <p className="text-xs text-charcoal/70 text-center mt-6">
          {isEmployee ? (
            <>Need a staff account? <Link to="/signup" className="underline text-navy">Register with PIN</Link></>
          ) : (
            <>New here? <Link to="/signup" className="underline text-navy">Create an account</Link></>
          )}
        </p>
      </div>
    </div>
  );
}
